import { PageProps, Recipe, Resource } from '@/types';
import { Head } from '@inertiajs/react';
import { AppLayout } from '@/Layouts';
import { useConfig, useLocalizedRoute } from '@/hooks';
import { UI } from '@/Components/UI';
import { AddButton, Author, BaseData, IngredientsList, InstructionsList, TagsList } from '@/Components/Specific';
import { structuredRecipe } from '@/utils';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { router } from '@inertiajs/react';

type ShowPageProps = PageProps<{
    recipe: Resource<Recipe>;
    can: {
        update: boolean;
        delete: boolean;
    }
}>

export default function({
                            recipe: { data: recipe },
                            can: { update: canUpdateRecipe, delete: canDeleteRecipe }
                        }: ShowPageProps) {
    const { i18n, t } = useTranslation(['recipes']);
    const { localizedRoute } = useLocalizedRoute();
    const { supportedLocales } = useConfig();

    const [servings, setServings] = useState(recipe.servings);

    const deleteRecipe = () => {
        const confirm = window.confirm(t('delete.confirm'));

        if (confirm) {
            router.visit(localizedRoute('recipes.destroy', { recipe: recipe.slug }), {
                method: 'delete'
            });
        }
    };

    return (
        <AppLayout>
            <Head>
                <title>{recipe.title}</title>

                <meta name={'title'} content={recipe.title} />
                <meta name={'description'} content={recipe.description} />
                {recipe.titleImage && (
                    <meta name={'image'} content={recipe.titleImage.originalUrl} />
                )}
                <meta name="robots" content="index,follow" />

                <link rel={'canonical'} href={localizedRoute('recipes.show', { recipe: recipe.slug })} />

                {supportedLocales
                    .filter((locale) => locale !== i18n.language)
                    .map((locale) => (
                        <link
                            rel={'alternate'}
                            hrefLang={locale}
                            href={localizedRoute('recipes.show', { recipe: recipe.slug }, locale)} />
                    ))}

                <script type={'application/ld+json'}>
                    {JSON.stringify(structuredRecipe(recipe))}
                </script>
            </Head>

            <div className={'flex justify-end gap-2 -mt-4 p-2'}>
                <AddButton
                    recipe={recipe}
                    buttonStyle={'text'} />

                {(canUpdateRecipe || canDeleteRecipe) && (
                    <>
                        {canUpdateRecipe && (
                            <UI.Button.Link
                                buttonStyle={'text'}
                                href={localizedRoute('recipes.edit', { recipe: recipe.slug })}>
                                <UI.Icon name={'edit'} />
                            </UI.Button.Link>
                        )}

                        {canDeleteRecipe && (
                            <UI.Button
                                buttonStyle={'text'}
                                onClick={deleteRecipe}>
                                <UI.Icon name={'delete'} />
                            </UI.Button>
                        )}
                    </>
                )}
            </div>

            <UI.Image
                className={'aspect-video md:aspect-24/9 w-full rounded-2xl object-cover'}
                image={recipe.titleImage}
                alt={recipe.title}
            />

            <UI.Heading level={1}>
                {recipe.title}
            </UI.Heading>

            <div className={'grid grid-cols-1 md:grid-cols-[2fr_1fr] lg:grid-cols-[3fr_1fr] gap-4'}>
                <div className={'grid grid-cols-2 auto-rows-fr md:grid-cols-3 gap-4'}>
                    <BaseData
                        recipe={recipe}
                        servings={servings}
                        setServings={setServings} />

                    <Author
                        author={recipe.author}
                        className={'md:hidden'} />
                </div>

                <div className={'flex flex-col gap-4 md:row-span-2'}>
                    <Author
                        author={recipe.author}
                        className={'hidden md:block'} />

                    {/* TODO */}
                    {/*<UI.Button.Link*/}
                    {/*    href={'#'}>*/}
                    {/*    Download*/}
                    {/*</UI.Button.Link>*/}

                    <TagsList
                        tags={recipe.tags} />
                </div>

                <div>
                    <p>
                        {recipe.description}
                    </p>

                    <UI.Heading
                        level={2}
                        className={'mb-4 mt-6'}>
                        {t('ingredients.title')}
                    </UI.Heading>

                    <IngredientsList
                        ingredients={recipe.ingredients}
                        initialServings={recipe.servings}
                        servings={servings} />

                    <UI.Heading
                        level={2}
                        className={'mb-4 mt-6'}>
                        {t('instructions.title')}
                    </UI.Heading>

                    <InstructionsList
                        instructions={recipe.instructions} />
                </div>
            </div>
        </AppLayout>
    );
}
